import { makeObservable, observable, action, flow, runInAction } from 'mobx';
import * as KatalMetrics from '@amzn/katal-metrics';
import initialMetricsPublisher from 'src/metrics';
import { logger } from 'src/logger';
import { INCORRECT_ZIP_CODE, STATES } from 'src/utils/Constants';
import { ProofOfDeliveryDetails } from 'src/stores/ProofOfDeliveryDetails';
import { getPackageDeliveryDetails } from 'src/utils/api';
import { AxiosResponse } from 'axios';
import { createContext } from 'react';

export interface SignatureDetails {
  signedBy: string;
}

export interface RecipientInformation {
  recipientName?: string | null;
  recipientDoorNumber?: string | null;
}

export class PackageDeliveryDetails {
  @observable protected _loadingState: string = STATES.DONE;
  @observable protected _trackingNumber = '';
  @observable protected _proofOfDeliveryDetails: ProofOfDeliveryDetails =
    new ProofOfDeliveryDetails();
  @observable protected _signatureDetails: SignatureDetails | null = null;
  @observable protected _recipientInformation: RecipientInformation | null = null;
  @observable protected _packageDropOffLocation = '';
  @observable protected _errorMessage = '';
  protected packageDeliveryDetail: AxiosResponse | undefined;

  constructor() {
    makeObservable(this);
  }

  @action
  setLoadingState(loadingState: string): void {
    this._loadingState = loadingState;
  }

  get getLoadingState(): string {
    return this._loadingState;
  }

  @action
  setTrackingNumber(trackingNumber: string): void {
    this._trackingNumber = trackingNumber;
  }

  get getTrackingNumber(): string {
    return this._trackingNumber;
  }

  @action
  setProofOfDeliveryDetails(proofOfDeliveryDetails: ProofOfDeliveryDetails): void {
    this._proofOfDeliveryDetails = proofOfDeliveryDetails;
  }

  get getProofOfDeliveryDetails(): ProofOfDeliveryDetails {
    return this._proofOfDeliveryDetails;
  }

  @action
  setSignatureDetails(signatureDetails: SignatureDetails | null): void {
    this._signatureDetails = signatureDetails;
  }

  get getSignatureDetails(): SignatureDetails | null {
    return this._signatureDetails;
  }

  @action
  setRecipientInformation(recipientInformation: RecipientInformation | null): void {
    this._recipientInformation = recipientInformation;
  }

  get getRecipientInformation(): RecipientInformation | null {
    return this._recipientInformation;
  }

  @action
  setPackageDropOffLocation(packageDropOffLocation: string): void {
    this._packageDropOffLocation = packageDropOffLocation;
  }

  get getPackageDropOffLocation(): string {
    return this._packageDropOffLocation;
  }

  @action
  setErrorMessage(errorMessage: string): void {
    this._errorMessage = errorMessage;
  }

  get getErrorMessage(): string {
    return this._errorMessage;
  }

  fetchPackageDeliveryDetails = flow(function* (
    this: PackageDeliveryDetails,
    trackingId: string,
    deliveryPostalCode: string,
    hasPhotoOnDelivery: boolean,
    hasSignatureOnDelivery: boolean,
    hasRecipientInformation: boolean
  ) {
    if (this._loadingState !== STATES.PENDING) {
      return;
    }

    // Determine which details to include based on package capabilities
    const includeDetails: string[] = [];
    if (hasSignatureOnDelivery) {
      includeDetails.push('SIGNATURE_ON_DELIVERY');
    } else if (hasRecipientInformation) {
      includeDetails.push('RECIPIENT_INFORMATION');
    } else if (hasPhotoOnDelivery) {
      includeDetails.push('PHOTO_ON_DELIVERY');
    }

    const fetchPackageDeliveryDetailsMetricsPublisher =
      initialMetricsPublisher.newChildActionPublisherForMethod('FetchPackageDeliveryDetails');
    const fetchPackageDeliveryDetailsHandlerMetric = new KatalMetrics.Metric.TimedAttempt(
      'FetchPackageDeliveryDetails'
    ).withMonitor();
    logger.info(`deliveryPostalCode', ${trackingId} + '-' +  ${deliveryPostalCode}`);
    logger.info(`includeDetails: ${includeDetails.join(', ')}`);
    try {
      runInAction(() => {
        this.setErrorMessage('');
      });

      this.packageDeliveryDetail = yield getPackageDeliveryDetails(
        trackingId, 
        deliveryPostalCode, 
        includeDetails.length > 0 ? includeDetails : undefined
      );

      if (this.packageDeliveryDetail && this.packageDeliveryDetail.data) {
        const responseData = this.packageDeliveryDetail.data;

        runInAction(() => {
          if (responseData.deliveryDetails && responseData.deliveryDetails.proofOfDeliveryDetails) {
            const podObject = new ProofOfDeliveryDetails();
            podObject.setImages(responseData.deliveryDetails.proofOfDeliveryDetails);
            this.setProofOfDeliveryDetails(podObject);
          }

          if (responseData.deliveryDetails && responseData.deliveryDetails.signatureDetails) {
            this.setSignatureDetails(responseData.deliveryDetails.signatureDetails);
          }

          if (responseData.deliveryDetails && responseData.deliveryDetails.recipientInformation) {
            this.setRecipientInformation(responseData.deliveryDetails.recipientInformation);
          }

          if (responseData.deliveryDetails && responseData.deliveryDetails.packageDropOffLocation) {
            this.setPackageDropOffLocation(responseData.deliveryDetails.packageDropOffLocation);
          }

          this.setTrackingNumber(responseData.trackingNumber);
          this._loadingState = STATES.DONE;
        });

        fetchPackageDeliveryDetailsHandlerMetric.setSuccess();
      } else {
        runInAction(() => {
          this._loadingState = STATES.ERROR;
        });
        fetchPackageDeliveryDetailsHandlerMetric.setFailure();
      }
    } catch (error: any) {
      runInAction(() => {
        this._loadingState = STATES.ERROR;
        if (error.response && error.response.status === 400) {
          this.setErrorMessage(INCORRECT_ZIP_CODE);
        } else {
          this.setErrorMessage(STATES.ERROR);
        }
      });

      fetchPackageDeliveryDetailsHandlerMetric.setFailure();
      logger.error(
        `Error fetching package delivery details for tracking id: ${trackingId} with error: ${error}`
      );
    } finally {
      fetchPackageDeliveryDetailsMetricsPublisher.publish(fetchPackageDeliveryDetailsHandlerMetric);
    }
  });
}

export default createContext(new PackageDeliveryDetails());
